import { useRef } from "react";
import type { StyleSpecification } from "maplibre-gl";
import {
  createPreviewSession,
  PreviewSession as Session,
} from "../../libs/simple/session";

type MapStyle = StyleSpecification & { id: string };

export type PreviewSession = Session<MapStyle>;

// The Simple panel's one session (see libs/simple/session.ts), created once
// and fed the style React last rendered.
export function usePreviewSession(
  mapStyle: MapStyle,
  onPreview: (mapStyle: MapStyle) => void,
  onCommit: (mapStyle: MapStyle) => void
): PreviewSession {
  const latest = useRef({ mapStyle, onPreview, onCommit });
  latest.current = { mapStyle, onPreview, onCommit };

  const session = useRef<PreviewSession | null>(null);
  if (!session.current) {
    session.current = createPreviewSession<MapStyle>({
      getStyle: () => latest.current.mapStyle,
      onPreview: (style) => latest.current.onPreview(style),
      onCommit: (style) => latest.current.onCommit(style),
    });
  }

  // Asked on every render so the session sees each style that reaches the
  // screen, and notices when its preview was replaced from outside.
  session.current.isPreviewing();

  return session.current;
}
